import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { getBottomAxisLayout } from './axisLayout';
import { useChartTheme } from './useChartTheme';
import './charts.css';

const DEFAULT_KEYS = ['value'];
const DEFAULT_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6'];
const DEFAULT_MARGIN = { top: 24, right: 24, bottom: 70, left: 80 };

export default function BarChart({
  data,
  indexBy = 'category',
  keys = DEFAULT_KEYS,
  colors,
  height = 300,
  title,
  valueFormatter,
  xLabel,
  yLabel,
  margin = DEFAULT_MARGIN,
}) {
  // Ensure keys is not null
  const chartKeys = keys || DEFAULT_KEYS;
  // Ensure colors is not null
  const chartColors = colors || DEFAULT_COLORS;
  // Ensure indexBy is not null
  const chartIndexBy = indexBy || 'category';
  const formatValue = valueFormatter || ((value) => new Intl.NumberFormat('en-US').format(Number(value) || 0));
  const theme = useChartTheme();

  const axisBottomLayout = React.useMemo(() => {
    return getBottomAxisLayout({
      labels: (data || []).map((d) => d[chartIndexBy]),
      tickRotation: -45,
      margin,
      hasLegend: Boolean(xLabel),
    });
  }, [data, chartIndexBy, margin, xLabel]);
  const effectiveMargin = axisBottomLayout.margin;

  // Ensure the chart container is tall enough to keep at least 150px of plot area.
  const effectiveHeight = Math.max(height, effectiveMargin.top + effectiveMargin.bottom + 150);

  return (
    <div className="chart-card">
      {title && <div className="chart-title">{title}</div>}
      <div className="chart-plot" style={{ height: effectiveHeight }}>
        <ResponsiveBar
          data={data || []}
          keys={chartKeys}
          indexBy={chartIndexBy}
          margin={effectiveMargin}
          theme={theme}
          colors={chartColors}
          borderColor={{ from: 'color', modifiers: [['darker', 1.2]] }}
          axisBottom={{
            tickRotation: -45,
            legend: xLabel,
            legendOffset: axisBottomLayout.legendOffset,
            legendPosition: 'middle',
            tickSize: 5,
            tickPadding: 5,
          }}
          axisLeft={{
            legend: yLabel,
            legendOffset: -72,
            legendPosition: 'middle',
            format: formatValue,
          }}
          labelSkipWidth={12}
          labelSkipHeight={12}
          labelFormat={formatValue}
          valueFormat={formatValue}
        />
      </div>
    </div>
  );
}
